import { z } from "zod";
import { layoutElementSchema } from "@/lib/layoutRenderer/schemas/layout.schema";
import type { DynamicFormSchema } from "../interfaces/dynamicForm.interface";

const ruleOperatorSchema = z.enum([
	"equals",
	"not_equals",
	"contains",
	"not_contains",
	"greater_than",
	"less_than",
	"greater_than_or_equal",
	"less_than_or_equal",
	"is_empty",
	"is_not_empty",
	"is_checked",
	"is_not_checked",
	"in",
	"not_in",
]);

const ruleActionTypeSchema = z.enum([
	"show_field",
	"hide_field",
	"enable_field",
	"disable_field",
	"set_required",
	"set_optional",
	"set_validation",
]);

const conditionLogicSchema = z.enum(["all", "any"]);

const fieldValidationSchema = z
	.object({
		min: z.number().optional(),
		max: z.number().optional(),
		required: z.boolean().optional(),
		pattern: z.string().optional(),
		minLength: z.number().optional(),
		maxLength: z.number().optional(),
		rows: z.number().optional(),
		custom: z.string().optional(),
	})
	.strict();

const formRuleConditionSchema = z
	.object({
		id: z.string(),
		fieldDefinitionId: z.string(),
		operator: ruleOperatorSchema,
		value: z
			.union([z.string(), z.number(), z.boolean(), z.array(z.string())])
			.optional(),
	})
	.strict();

const formRuleActionSchema = z
	.object({
		id: z.string(),
		type: ruleActionTypeSchema,
		targetFieldDefinitionId: z.string(),
		value: z
			.union([
				z.string(),
				z.number(),
				z.boolean(),
				z.array(z.string()),
				fieldValidationSchema,
			])
			.optional(),
	})
	.strict();

const formRuleSchema = z
	.object({
		id: z.string(),
		name: z.string(),
		description: z.string().optional(),
		enabled: z.boolean().optional(),
		conditionLogic: conditionLogicSchema,
		conditions: z.array(formRuleConditionSchema),
		actions: z.array(formRuleActionSchema),
		createdAt: z.string().optional(),
		updatedAt: z.string().optional(),
	})
	.strict();

const persistedFieldDefinitionSchema = z
	.object({
		id: z.string(),
		name: z.string(),
		label: z.string().nullable(),
		dataType: z.string(),
		required: z.boolean().optional(),
		validationRules: z.unknown().nullable().optional(),
	})
	.passthrough();

/**
 * Current {@link DynamicFormSchema} JSON document: `fieldDefinitions` + `layoutJson`.
 */
export const dynamicFormDocumentSchema = z
	.object({
		id: z.string(),
		title: z.string().optional(),
		description: z.string().optional(),
		fieldDefinitions: z.array(persistedFieldDefinitionSchema),
		layoutJson: layoutElementSchema,
		rules: z.array(formRuleSchema).optional(),
	})
	.strict();

export type DynamicFormSchemaValidationIssue = {
	path: string;
	code: string;
	message: string;
};

function zodIssuesToValidationIssues(
	issues: z.ZodIssue[],
): DynamicFormSchemaValidationIssue[] {
	return issues.map((issue) => ({
		path: issue.path.map(String).join("."),
		code: issue.code,
		message: issue.message,
	}));
}

/**
 * Validates unknown JSON as a {@link DynamicFormSchema}: required keys present, only known keys
 * at the document root (strict), and a valid layout tree.
 */
export function validateDynamicFormSchemaJson(value: unknown):
	| {
			ok: true;
			schema: DynamicFormSchema;
	  }
	| {
			ok: false;
			issues: DynamicFormSchemaValidationIssue[];
	  } {
	const result = dynamicFormDocumentSchema.safeParse(value);
	if (result.success) {
		return { ok: true, schema: result.data as unknown as DynamicFormSchema };
	}
	return {
		ok: false,
		issues: zodIssuesToValidationIssues(result.error.issues),
	};
}
